import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useAiCoach} from '../core/AiCoachContext'

const QUICK_PROMPTS = [
  'How are my active setups?',
  "How's my R this month?",
  "What's BTC doing right now?",
] as const

const FAB_SIZE = 58

const FAB_STYLE: React.CSSProperties = {
  position: 'fixed',
  width: FAB_SIZE,
  height: FAB_SIZE,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(232, 64, 64, 1) 0%, rgba(199, 36, 110, 1) 100%)',
  color: '#fff',
  border: 'none',
  boxShadow:
    '0 8px 24px rgba(232, 64, 64, 0.28), 0 2px 6px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1040,
  transition: 'transform 0.16s ease, box-shadow 0.16s ease',
}

const POPOVER_STYLE: React.CSSProperties = {
  position: 'fixed',
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 14,
  padding: 12,
  boxShadow: '0 18px 48px rgba(0, 0, 0, 0.22)',
  zIndex: 1041,
  minWidth: 240,
  maxWidth: 280,
}

const POPOVER_TITLE_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
}

const POPOVER_PROMPT_STYLE: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: '1px solid var(--card-border)',
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 12,
  color: 'var(--text-primary, inherit)',
  cursor: 'pointer',
  marginTop: 6,
  transition: 'background 0.12s, border-color 0.12s',
}

/**
 * Floating AI Coach button mounted globally in MasterLayout.
 *
 * - Hidden on `/ai-coach` (would just duplicate the full-page panel).
 * - On hover, exposes a small popover with quick-prompt shortcuts.
 * - Bottom offset is bumped on small screens so the FAB doesn't sit
 *   under the mobile bottom nav / safe-area inset.
 */
export const AiCoachWidget: React.FC = () => {
  const {pathname} = useLocation()
  const {openCoach} = useAiCoach()
  const [hovered, setHovered] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    const sync = () => setIsMobile(mql.matches)
    sync()
    if (mql.addEventListener) {
      mql.addEventListener('change', sync)
      return () => mql.removeEventListener('change', sync)
    }
    // Safari < 14 fallback
    mql.addListener(sync)
    return () => mql.removeListener(sync)
  }, [])

  if (pathname.startsWith('/ai-coach')) return null

  const fabRight = isMobile ? 16 : 24
  const fabBottom = isMobile ? 88 : 28
  const popRight = fabRight + FAB_SIZE + 12
  const popBottom = fabBottom

  return (
    <>
      <button
        type='button'
        aria-label='AI Coach'
        title='AI Coach (⌘K)'
        onClick={() => openCoach()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{
          ...FAB_STYLE,
          right: fabRight,
          bottom: fabBottom,
          transform: hovered ? 'scale(1.06)' : 'scale(1)',
        }}
      >
        <i className='bi bi-stars' style={{fontSize: 24, color: '#fff'}} />
      </button>

      {hovered && !isMobile && (
        <div
          style={{...POPOVER_STYLE, right: popRight, bottom: popBottom}}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div style={POPOVER_TITLE_STYLE}>
            <i className='bi bi-stars' style={{color: 'rgba(232, 64, 64, 0.95)'}} />
            Ask the AI Coach
          </div>
          {QUICK_PROMPTS.map((q) => (
            <button
              key={q}
              type='button'
              onClick={() => openCoach(q)}
              style={POPOVER_PROMPT_STYLE}
              onMouseDown={(e) => e.preventDefault()}
            >
              {q}
            </button>
          ))}
          <div
            style={{
              marginTop: 10,
              fontSize: 10,
              color: 'var(--text-muted)',
              textAlign: 'right',
            }}
          >
            Tip: press ⌘K from anywhere
          </div>
        </div>
      )}
    </>
  )
}

export default AiCoachWidget
